import { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { Form as FormAntd, message, Table } from 'antd';
import { DownloadOutlined, PlusOutlined } from '@ant-design/icons';
import { ActionButton } from '@hdi-seguros/web-components.action-button';

import {
  ColSize,
  ContentSection,
  Form,
  RowSize,
  SelectForm,
} from '../../../../../../../../../shared/components';

import {
  GenericDataFormContext,
  QUOTATION_BUTTON,
  QuotationInfoContext,
  StepOneFormContext,
} from '../../../../../shared';
import { StepTwoFormContext } from '../../../StepTwo/contexts/StepTwoForm';
import { StepThreeFormContext } from '../../contexts/StepThreeForm';

import {
  formattedPrice,
  ValidateForm,
} from '../../../../../../../../../shared/utils';
import { useGenerateDataTable } from './utils/generate-data-table';

import { HomePaymentMethods } from '../PaymentMethods';

import { WebHomeQuotation } from '../../../../../../../../../shared/interfaces';

import './index.css';
import { getQuoteDocumentPDF, transformToPdfQuote } from '../../../../../utils';
import { BranchDocumentsURL, BudgetType } from '../../../../../utils/quotation-documents.enum';
import { QuotationContextData } from '../../../../../../../../../shared/interfaces/services/quote-pdf-service';
import { CoveragesHomeSection } from '../CoverageHomeSection';

interface HomeQuotationFormByTabClosedProps {
  tabKey: number;
}

export const HomeQuotationFormByTabClosed: FC<
  HomeQuotationFormByTabClosedProps
> = ({ tabKey }) => {
  const validateForm = new ValidateForm();
  const [quotationResponseClosedInfo, setQuotationResponseClosedInfo] = useState<any>(null);

  const { planOptionsBySelectedPlan, stepTwoForm, activeKey, housingTypeOptionsPerPlan } = useContext(StepTwoFormContext);
  const { columnsDataTitles, columnsDataTitlesSummary, rowDataTable } =
    useGenerateDataTable();
  const {
    quotationResponseClosed,
    stepThreeHomeQuotationFormCoverages,
    stepThreeHomeQuotationFormSummary,
    labelButton,
    setLabelButton,
    planSelectedSummary,
    setPlanSelectedSummary,
    executeQuotationForSelectedClosePlan,
    quotationResponse,
    homePaymentMethodsForm,
    homeQuotationComponents,
  } = useContext(StepThreeFormContext);
  const { quotationInfo } = useContext(QuotationInfoContext);
  
  const { setCanShowSpinner, plansSelected, setPlansSelected, 
    provinceList, allLocalitiesPerProvince, paymentMethodSectionForm, allCoveragesPerPlan, paymentMethodsList,
    ivaConditionList, paymentMethods } = useContext(GenericDataFormContext);

  const handleSelectPlanForQuotation = useCallback((plan: string) => {
    if (plan === planSelectedSummary) {
      setPlanSelectedSummary('');
      setLabelButton(QUOTATION_BUTTON.COTIZAR)
      return;
    }
    setPlanSelectedSummary(plan);
    const planInfo: WebHomeQuotation = quotationResponseClosed?.find(
          (el) => el.planSelected === plan
        )?.response!;
        setQuotationResponseClosedInfo(planInfo);
  }, [
    planSelectedSummary,
    setPlanSelectedSummary,
    setLabelButton,
    quotationResponseClosed
  ]);

  const productIds = Array.from(
    new Set(plansSelected?.map((plan) => plan as string))
  );

  useEffect(() => {
    if (labelButton === QUOTATION_BUTTON.RECOTIZAR && planSelectedSummary !== '') {
      setPlanSelectedSummary('');
    }
  }, [labelButton]);

  const { stepOneForm, clientSelected } = useContext(StepOneFormContext);

  const ctx: QuotationContextData = {
    stepOneForm,
    clientSelected, planSelectedSummary,
    stepTwoForm, activeKey, housingTypeOptionsPerPlan, planOptionsBySelectedPlan,
    quotationResponse, homePaymentMethodsForm,quotationResponseClosedInfo,
    quotationInfo, paymentMethodsList, homeQuotationComponents,
    provinceList, allLocalitiesPerProvince, paymentMethodSectionForm, ivaConditionList, paymentMethods, allCoveragesPerPlan,
    branchDocumentsURL: BranchDocumentsURL.HOGAR
  };

  const columnsDataSummary = useMemo(() => [
    {
      key: 1,
      description: 'Total Suma Asegurada',
      ...productIds.reduce((acc: { [key: string]: React.ReactNode }, productId) => {
        const plan: WebHomeQuotation = quotationResponseClosed?.find(
          (el) => el.planSelected === productId
        )?.response!;

        const key = `sumaAsegurada-${productId}`;

        acc[key] = (
          <div
            className={
              productId === planSelectedSummary && labelButton !== QUOTATION_BUTTON.RECOTIZAR
                ? 'plan-selected-bg'
                : ''
            }
          >
            <b>{
              plan?.sumaAsegurada && labelButton !== QUOTATION_BUTTON.RECOTIZAR ?
                `$${formattedPrice(plan.sumaAsegurada)}` :
                `Recotizar`
            }</b>
          </div>
        );

        return acc;
      }, {}),
    },
    {
      key: 2,
      description: 'Comisión',
      ...productIds.reduce((acc: { [key: string]: React.ReactNode }, productId) => {
        const plan: WebHomeQuotation = quotationResponseClosed?.find(
          (el) => el.planSelected === productId
        )?.response!;

        const key = `sumaAsegurada-${productId}`;

        acc[key] = (
          <div
            className={
              productId === planSelectedSummary && labelButton !== QUOTATION_BUTTON.RECOTIZAR
                ? 'plan-selected-bg'
                : ''
            }
          >
            <span>{
              plan?.condicionesComerciales.impComision && labelButton !== QUOTATION_BUTTON.RECOTIZAR ?
                `$${formattedPrice(plan.condicionesComerciales.impComision)}` :
                `Recotizar`
            }</span>
          </div>
        );
        return acc;
      }, {}),
    },
    {
      key: 3,
      description: 'Premio',
      ...productIds.reduce((acc: { [key: string]: React.ReactNode }, productId) => {
        const plan: WebHomeQuotation = quotationResponseClosed?.find(
          (el) => el.planSelected === productId
        )?.response!;

        const key = `sumaAsegurada-${productId}`;

        acc[key] = (
          <div
            className={
              productId === planSelectedSummary && labelButton !== QUOTATION_BUTTON.RECOTIZAR
                ? 'plan-selected-bg'
                : ''
            }
          >
            <span>{
              plan?.premio && labelButton !== QUOTATION_BUTTON.RECOTIZAR ?
                `$${formattedPrice(plan.premio)}` :
                `Recotizar`
            }</span>
          </div>
        );
        return acc;
      }, {}),
    },
    {
      key: 4,
      description: 'Acciones',

      ...productIds.reduce((acc: { [key: string]: React.ReactNode }, productId) => {
        const plan: WebHomeQuotation = quotationResponseClosed?.find(
          (el) => el.planSelected === productId
        )?.response!;
        
        const key = `sumaAsegurada-${productId}`;

        const planWasSelected: boolean = 
        plan?.componentes[0]?.producto !== undefined &&
        planSelectedSummary !== undefined &&
        plan?.componentes[0]?.producto === planSelectedSummary && labelButton !== QUOTATION_BUTTON.RECOTIZAR

        acc[key] = (
          <div
            className={`buttons-list-home ${ planWasSelected ? 'plan-selected-bg' : '' }`}
          >
            {plan ? (
              labelButton === QUOTATION_BUTTON.RECOTIZAR ? (
                'Recotizar'
              ) : (
                <>
                  <ActionButton
                    className='btn-accent-outline-barbuss'
                    icon={<DownloadOutlined />}
                    label='Productor'
                    htmlType='submit'
                    disabled={!(plan?.componentes[0]?.producto === planSelectedSummary)}
                    type='secondary'
                    onClick={() => getQuoteDocumentPDF(transformToPdfQuote(ctx, BudgetType.PRODUCTOR), BranchDocumentsURL.HOGAR)}
                  />
                  <ActionButton
                    className='btn-accent-outline-barbuss'
                    icon={<DownloadOutlined />}
                    label='Cliente'
                    disabled={!(plan?.componentes[0]?.producto === planSelectedSummary)}
                    htmlType='submit'
                    color='secondaryDefault'
                    type='secondary'
                    onClick={() => getQuoteDocumentPDF(transformToPdfQuote(ctx, BudgetType.CLIENTE), BranchDocumentsURL.HOGAR)}
                  />
                  <ActionButton
                    className={plan?.componentes[0].producto === planSelectedSummary ? 'btn-principal-barbuss' : 'btn-principal-outline-barbuss'}
                    label={
                      plan?.componentes[0].producto === planSelectedSummary
                        ? 'Seleccionado'
                        : 'Seleccionar'
                    }
                    htmlType='submit'
                    type={
                      plan?.componentes[0].producto === planSelectedSummary
                        ? 'primary'
                        : 'secondary'
                    }
                    onClick={() =>
                      handleSelectPlanForQuotation(
                        plan?.componentes[0].producto
                      )
                    }
                  />
                </>
              )
            ) : (
              <span>Recotizar</span>
            )}
          </div>
        );
        return acc;
      }, {}),
    },
    ],
    [
      planSelectedSummary,
      productIds,
      quotationResponseClosed,
      labelButton,
      handleSelectPlanForQuotation,
    ]
  )
  
  const addPlanWithRow = useCallback(() => {
    setCanShowSpinner(true);
    try {

      const planInputName = `plan-tab-${tabKey}`;
      const codePlanSelected =
        stepThreeHomeQuotationFormCoverages.getFieldValue(planInputName);

      if (codePlanSelected) {
        if (plansSelected.length >= 3) {
          message.error('No puedes agregar más de 3 planes');
          return;
        } else if (
          plansSelected.indexOf(codePlanSelected) != -1
        ) {
          message.error('El plan seleccionado ya fue agregado');
          return;
        }

        setPlansSelected((prevPlans) => [...prevPlans, codePlanSelected]);
      }

    } catch (error) {
    } finally {
      setCanShowSpinner(false);
    }
  }, [setPlansSelected, plansSelected, stepThreeHomeQuotationFormCoverages, setCanShowSpinner]);

  const onChangeForm = useCallback(
    (changedFields: any[]) => {
      changedFields.forEach((field) => {
        if (field.name[0].includes('aditional') && quotationResponseClosed.length) {
          setLabelButton(QUOTATION_BUTTON.RECOTIZAR);
        }
      });
    },
    [setLabelButton, quotationResponseClosed]
  );

  useEffect(() => {
    if (planSelectedSummary) {
      executeQuotationForSelectedClosePlan();
    }
  }, [planSelectedSummary]);

  return (
    <div className='mt-1 container-table-quotation-closed'>
      <Form
        formProps={{
          form: stepThreeHomeQuotationFormCoverages,
          name: 'step-three-home-quotation-form-coverages',
          onFieldsChange: onChangeForm,
        }}
      >
        <ContentSection level='h3' title='Planes'>
          <RowSize>
            <ColSize size={25}>
              <SelectForm
              formProps={{
                label: 'Lista de Planes (Max. 3 planes)',
                name: `plan-tab-${tabKey}`,
                rules: validateForm.generateRules({
                  label: 'lista de planes',
                  rules: ['required'],
                }),
              }}
              selectProps={{
                options: planOptionsBySelectedPlan,
              }}
            />
            </ColSize>

            <FormAntd.Item label=' '>
              <ColSize size={25}>
                <ActionButton
                  className='btn-accent-outline-barbuss'
                  icon={<PlusOutlined />}
                  label='Agregar plan'
                  htmlType='submit'
                  type='secondary'
                  onClick={addPlanWithRow}
                />
              </ColSize>
            </FormAntd.Item>
          </RowSize>
        </ContentSection>
        <CoveragesHomeSection tabKey={tabKey} />

        {(columnsDataTitles ?? []).length > 1 ? (
          <ContentSection level='h3' title='Coberturas' hasCard={false}>
            <Table
              bordered
              columns={columnsDataTitles}
              dataSource={rowDataTable}
              pagination={false}
              className='w-100'
            />
          </ContentSection>
        ) : <></>}
      </Form>

      {(columnsDataTitles ?? []).length > 1 && quotationResponseClosed.length ? (
        <>
          <HomePaymentMethods />
          <Form
            formProps={{
              form: stepThreeHomeQuotationFormSummary,
              name: 'step-three-home-quotation-form-summary',
            }}
          >
            <ContentSection level='h3' title='Resumen' hasCard={false}>
              <Table
                bordered
                columns={columnsDataTitlesSummary}
                dataSource={columnsDataSummary}
                pagination={false}
                className='w-100'
              />
            </ContentSection>
          </Form>
        </>
      ) : <></>}
    </div>
  );
};